/**
 * FOOTER - Control Plane Sync
 * ===========================
 * 
 * Footer avec liens filtrés par Control Plane.
 * 
 * RÈGLES:
 * - Pages légales → toujours visibles
 * - Autres pages → filtrées via Control Plane
 * 
 * SAFE MODE: Read-only, aucune modification de routes.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useRetailerPages } from '../hooks/useRetailerPages';
import { BrandLogo } from '../lib/branding';

const Footer = () => {
  // Control Plane pour les liens dynamiques
  const { hubPages, cmsPages, loading } = useRetailerPages();
  
  // Pages légales (statiques - toujours affichées)
  const legalLinks = [
    { href: '/mentions-legales', label: 'Mentions Légales' },
    { href: '/cgu', label: "Conditions Générales d'Utilisation" },
    { href: '/politique-confidentialite', label: 'Politique de Confidentialité' }
  ];
  
  // Pages métier dynamiques (Control Plane)
  const dynamicLinks = React.useMemo(() => {
    const links = [];
    
    // Ajouter les HUB visibles
    hubPages.forEach(page => {
      links.push({
        href: page.route,
        label: page.title,
        order: page.display_order
      });
    });
    
    // Ajouter les pages CMS visibles
    cmsPages.forEach(page => {
      links.push({
        href: page.route,
        label: page.title,
        order: page.display_order
      });
    });
    
    return links.sort((a, b) => a.order - b.order).slice(0, 6); // Max 6 liens
  }, [hubPages, cmsPages]);

  return (
    <footer className="ludo-footer text-white py-10 mt-16" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo et description */}
          <div>
            <div className="flex items-center mb-4">
              <BrandLogo size="md" variant="light" />
            </div>
            <p className="text-white/50 text-sm leading-relaxed">
              La plateforme B2B de référence pour les professionnels du jeu de société et du TCG.
            </p>
          </div>

          {/* Navigation (Control Plane) */}
          <div>
            <h3 className="font-heading font-semibold mb-4 text-sm tracking-wide uppercase text-white/70">Navigation</h3>
            {loading ? (
              <div className="space-y-2.5">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-4 w-24 bg-white/10 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <ul className="space-y-2.5 text-sm">
                {dynamicLinks.map((link) => (
                  <li key={link.href}>
                    <Link 
                      to={link.href} 
                      className="text-white/50 hover:text-white transition-colors duration-200"
                      data-testid={`footer-link-${link.href.replace(/\//g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Liens légaux (statiques) */}
          <div>
            <h3 className="font-heading font-semibold mb-4 text-sm tracking-wide uppercase text-white/70">Informations Légales</h3>
            <ul className="space-y-2.5 text-sm">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href} 
                    className="text-white/50 hover:text-white transition-colors duration-200"
                    data-testid={`footer-legal-${link.href.replace(/\//g, '-')}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-semibold mb-4 text-sm tracking-wide uppercase text-white/70">Contact</h3>
            <ul className="space-y-2 text-sm text-white/50">
              <li>contact@gamesvisor.com</li>
              <li>+33 1 23 45 67 89</li>
              <li>Paris, France</li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 text-center text-xs text-white/30">
          <p>&copy; {new Date().getFullYear()} Games Visor. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
